// @greenlight-spec: 28-it-dashboard-ui.md
// @greenlight-spec: 11-observability.md#audit-log
// Row-detail drawer for one audit event: a desktop slide-over / mobile sheet
// (via GLDrawer) holding the full record the table grid omits — the
// raw action constant, target ids, actor email/session, request id, and the
// `details` payload. This is where the machine-readable fields live so the table
// row can stay a single human-readable line.
import * as React from 'react';

import { GLDrawer } from '@/components/greenlight/GLDrawer';
import { MonoValue } from '@/components/greenlight/MonoValue';
import { relativeTime } from '@/components/greenlight/relativeTime';
import { StatusBadge, type StatusBadgeVariant } from '@/components/greenlight/StatusBadge';
import { auditSummary } from '@/components/greenlight/auditSummary';
import { UserAvatar } from '@/components/greenlight/UserAvatar';
import { AuditEventSchema } from '@greenlight/contract';
import type { z } from 'zod';

type AuditEvent = z.infer<typeof AuditEventSchema>;

const OUTCOME_META: Record<AuditEvent['outcome'], { variant: StatusBadgeVariant; label: string }> =
  {
    success: { variant: 'active', label: 'Success' },
    failure: { variant: 'failed', label: 'Failure' },
  };

export interface AuditEventInspectorProps {
  event: AuditEvent | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Resolved person name for the actor, or null for agent-only / system rows. */
  actorName: string | null;
  actorEmail: string | null;
  /** Resolved app slug, or null when the event is not app-scoped. */
  appLabel: string | null;
}

function SectionHeader({ children }: { children: React.ReactNode }): React.JSX.Element {
  return (
    <div className="mb-2 font-mono text-[10px] font-medium uppercase tracking-[0.08em] text-muted-foreground">
      {children}
    </div>
  );
}

/** Inset card shell wrapping a group of label/value rows (a `<dl>`, since its
 *  rows render `<dt>`/`<dd>` pairs). */
function InsetCard({ children }: { children: React.ReactNode }): React.JSX.Element {
  return (
    <dl className="overflow-hidden rounded-gl-lg border border-border bg-muted/40 [&>*+*]:border-t [&>*+*]:border-border">
      {children}
    </dl>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }): React.JSX.Element {
  return (
    <div className="flex items-start justify-between gap-4 px-3.5 py-3">
      <dt className="shrink-0 pt-0.5 text-[13px] text-muted-foreground">{label}</dt>
      <dd className="min-w-0 text-right text-[13.5px] font-medium text-foreground">{children}</dd>
    </div>
  );
}

export function AuditEventInspector({
  event,
  open,
  onOpenChange,
  actorName,
  actorEmail,
  appLabel,
}: AuditEventInspectorProps): React.JSX.Element {
  const summary = event ? auditSummary(event) : '';
  const outcome = event ? OUTCOME_META[event.outcome] : null;

  const title = event ? (
    <div className="flex min-w-0 flex-col gap-1.5">
      <span className="font-mono text-[10px] uppercase tracking-[0.08em] text-muted-foreground">
        Audit record
      </span>
      <span className="text-[18px] font-bold leading-snug tracking-tight text-foreground">
        {summary}
      </span>
      {outcome && <StatusBadge variant={outcome.variant}>{outcome.label}</StatusBadge>}
    </div>
  ) : (
    ''
  );

  return (
    <GLDrawer
      open={open}
      onOpenChange={onOpenChange}
      variant="panel"
      title={title}
      srDescription="Audit event details"
      storageKey="gl-audit-panel-width"
      // Inspector follows the page's md (768px) collapse so overlay and page
      // switch layouts together — tables behind it collapse to card lists at md.
      mobileBreakpoint={768}
    >
      {event && (
        <div className="space-y-5">
          {/* EVENT group */}
          <section>
            <SectionHeader>Event</SectionHeader>
            <InsetCard>
              <Row label="When">
                <div>
                  <div className="font-medium">{relativeTime(event.createdAt)}</div>
                  <div className="font-mono text-[11.5px] text-muted-foreground">
                    {event.createdAt}
                  </div>
                </div>
              </Row>
              <Row label="Actor">
                {actorName ? (
                  <div className="flex flex-col items-end">
                    <div className="flex items-center justify-end gap-2.5">
                      <UserAvatar name={actorName} size={22} aria-hidden />
                      <span className="font-bold">{actorName}</span>
                    </div>
                    {actorEmail && (
                      <span className="font-mono text-[11.5px] text-muted-foreground">
                        {actorEmail}
                      </span>
                    )}
                    {event.actorAgentSessionId && (
                      <span className="font-mono text-[11px] text-muted-foreground">via agent</span>
                    )}
                  </div>
                ) : event.actorAgentSessionId ? (
                  'Builder agent'
                ) : (
                  'System'
                )}
              </Row>
              {/* Role/channel are omitted (not shown as "—") on rows written
                  before the write path set actor_role/actor_channel. */}
              {event.actorRole && <Row label="Role">{event.actorRole}</Row>}
              {event.actorChannel && <Row label="Channel">{event.actorChannel}</Row>}
              <Row label="App">{appLabel ? <MonoValue>{appLabel}</MonoValue> : '—'}</Row>
            </InsetCard>
          </section>

          {/* PROXY REQUEST group — only for rows the proxy wrote HTTP detail for
              (docs/11 §Log format); gated on `method` alone so the section never
              renders half-empty. */}
          {event.method && (
            <section>
              <SectionHeader>Proxy request</SectionHeader>
              <InsetCard>
                <Row label="Request">
                  <MonoValue className="break-all">
                    {event.method} {event.path}
                  </MonoValue>
                </Row>
                {event.responseStatus !== undefined && (
                  <Row label="Status">
                    <MonoValue>{event.responseStatus}</MonoValue>
                  </Row>
                )}
                {event.durationMs !== undefined && (
                  <Row label="Duration">{event.durationMs} ms</Row>
                )}
                {event.credentialUsed && (
                  <Row label="Credential used">
                    <MonoValue copyable className="break-all">
                      {event.credentialUsed}
                    </MonoValue>
                  </Row>
                )}
              </InsetCard>
            </section>
          )}

          {/* TARGET group — only when resourceId or resourceType is present */}
          {(event.resourceId || event.resourceType) && (
            <section>
              <SectionHeader>Target</SectionHeader>
              <InsetCard>
                {event.resourceId && (
                  <Row label="Resource">
                    <MonoValue copyable className="break-all">
                      {event.resourceId}
                    </MonoValue>
                  </Row>
                )}
                <Row label="Kind">{event.resourceType}</Row>
              </InsetCard>
            </section>
          )}

          {/* RAW RECORD group */}
          <section>
            <SectionHeader>Raw record</SectionHeader>
            <InsetCard>
              <Row label="Action">
                <MonoValue copyable className="break-all">
                  {event.action}
                </MonoValue>
              </Row>
              <Row label="Request ID">
                <MonoValue copyable className="break-all">
                  {event.requestId}
                </MonoValue>
              </Row>
              {event.actorAgentSessionId && (
                <Row label="Agent session">
                  <MonoValue copyable className="break-all">
                    {event.actorAgentSessionId}
                  </MonoValue>
                </Row>
              )}
              <Row label="Event ID">
                <MonoValue copyable className="break-all">
                  {event.id}
                </MonoValue>
              </Row>
            </InsetCard>
            {event.details != null && (
              <pre className="mt-3 overflow-x-auto rounded-gl border border-border bg-muted/50 p-3 font-mono text-[11px] text-foreground">
                {JSON.stringify(event.details, null, 2)}
              </pre>
            )}
          </section>
        </div>
      )}
    </GLDrawer>
  );
}
